import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { cloneDeep, isEmpty, isEqual } from 'lodash'
import getText from '../../shared/i18n/labels'

import {
  Table,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell
} from '@table-library/react-table-library/table'
import Sortable from 'sortablejs'

import { useRowSelect, SelectClickTypes } from '@table-library/react-table-library/select'
import { useTheme } from '@table-library/react-table-library/theme'
import { DEFAULT_OPTIONS, getTheme } from '@table-library/react-table-library/material-ui'
import { TableUtils, baseSearchObject, fieldTypes } from './TableUtils'
import NumberFormat from 'react-number-format'
import { StarCell } from './StarCell'
import { getTableStorage } from './TableStorage'
import { DropdownMultipleSelect } from '../form/lists/DropdownMultipleSelect'
import { formatDateForDisplay, generateUniqueKey, isFontBigger } from '../Utility'
import { StarCheckbox } from '../form/input-boxes/StarCheckbox'
import { StarTableTop } from './StarTableTop'
import { TableSkeleton } from './StarTableSkeleton'
import { useHistory } from 'react-router-dom'

import { StarTooltip } from '../tooltips/Tooltip'
import { BwmSelect } from '../form/BwmSelect'
import { BREAKPOINT_HEIGHT_FORM_ON_BOTTOM } from '../../../config/Constants'

/**
 * Componente multifunzione per liste di elementi.
 *
 * per forzare un reload della lista(ad es in caso di salvataggi di nuove entità), ricalcolare la key del componente
 * @param {*} param0
 * @returns
 */
export function StarTable({
  id = undefined,
  routePath = null,
  headerComponent = undefined,
  headerColumns = [], //colonne header componibili tramite TableUtils.composeHeader()
  checkedItems = [],
  rows = [], //righe componibili tramite TableUtils.composeRow(TableUtils.composeCell())
  withCheckBoxes = false, //indica se le righe sono selezionabili
  onClickCheck = (ids) => {}, //callback su click dei checkbox, passa la lista di id selezionati
  loading = false, //indica se mostrare lo spinner
  tableConfigurationKey = 'star-table', //chiave localStorage della configurazione della tabella, per determinare ad es. quali colonne mostrare
  searchCallBack, //callback di ricerca
  onExecutedSearch = () => {}, //funzione eseguita a ricerca completata, fornisce il contenuto della ricerca
  searchObjectPrototype: searchObject = {}, //searchObject di base. Una copia viene conservata al montaggio per riportare la lista allo stato iniziale
  setSearchObject = (searchObjet) => {}, //setter per searchObject
  usePagination = true,
  version = '0',
  useDrag = true,
  useResize = true,
  useSort = true,
  useReset = null,
  useHover = true,
  actionsComponentOnTop = true,
  paginationOnBottom = true,
  hideSearch = null,
  sortStyle = { marginLeft: '5px' },
  dialogComponent = null,
  actionsComponent = null,
  perPageOptions = null,
  setId = null,
  onClickRow = null,
  onDoubleClickRow = () => {},
  onHideSearch = undefined,
  formOnBottom = false,
  formComponent: DynamicFormComponent,
  formComponentProps = {},
  additionalHeaderProps = {},
  showResetFilter = false,
  resetFilterOnBottom = true,
  rowClass = (rowData) => '',
  extraComponentBottomLeft = null,
  height = '24px',
  offset = 250,
  extraOffset = 0,
  breakpointFormOnBottom = BREAKPOINT_HEIGHT_FORM_ON_BOTTOM, // breakpoint per passare alla visualizzazione con overflow (height 100%)
  tableContainerWidth = '100%'
}) {
  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const tableConfiguration = tableConfigurationKey
    ? getTableStorage(tableConfigurationKey, version)
    : null
  const [hiddenColumns, setHiddenColumns] = useState(
    tableConfiguration ? tableConfiguration?.getHiddenColumns() : []
  )
  const [columns, setColumns] = useState(headerColumns)
  const [initialColumns, setInitialColumns] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [defaultSort, setDefaultSort] = useState('id')
  const [tableKey, setTableKey] = useState()
  const [headerKey, setHeaderKey] = useState()
  const [totalPages, setTotalPages] = useState(0)
  const [initialSearchObject, setInitialSearchObject] = useState({}) //oggetto di ricerca iniziale
  const [searchTerms, setSearchTerms] = useState({}) //oggetto che contiene i termini di ricerca tramite header
  const [headerSizes, setHeaderSizes] = useState('')
  const [hideFilters, setHideFilters] = useState(true)
  const [_, setClickTimeout] = useState(null)
  const [keyboardNavigation, setKeyboardNavigation] = useState(false)
  const timerRef = useRef(null)

  const [dateFilters, setDateFilters] = useState({})

  const timeout = useRef()
  const tableRef = useRef(null)
  const tableContainerRef = useRef(null)
  const [changePage, setChangePage] = useState()
  const [isScrolling, setIsScrolling] = useState(false)
  const history = useHistory()
  const firstRender = useRef(true)

  const propsRef = useRef({ rows, id })

  useEffect(() => {
    if (!changePage) return
    onChangePage(searchObject)
    setChangePage()
  }, [changePage])

  useEffect(() => {
    propsRef.current.rows = rows
    propsRef.current.id = id
    propsRef.current.totalPages = totalPages
    propsRef.current.searchObject = searchObject
    propsRef.current.routePath = routePath
  }, [rows, id, searchObject, totalPages, routePath])

  //esegue ricerca iniziale, e conserva il searchObject iniziale
  useEffect(() => {
    handleResize()
    setInitialSearchObject(searchObject)

    if (tableConfiguration) {
      setColumns(
        TableUtils.updateHeadersOrder(
          columns,
          tableConfiguration.getColumnOrder(),
          tableConfiguration
        )
      )
      setInitialColumns(
        TableUtils.updateHeadersOrder(
          headerColumns,
          tableConfiguration.getColumnOrder(),
          tableConfiguration
        )
      )
    }

    if (tableConfiguration?.getColumnSizes()) {
      setHeaderSizes(tableConfiguration.getColumnSizes())
    } else {
      setHeaderSizes(
        TableUtils.prepareHeaderSizes(columns, hiddenColumns, withCheckBoxes, tableConfiguration)
      )
    }
    setSearchTerms(searchObject)
    setDefaultSort(searchObject.sortField)

    setTableKey(generateUniqueKey(tableConfigurationKey))

    window.addEventListener('resize', handleResize)
    window.addEventListener('keydown', handleKey)
    window.addEventListener('mousemove', removeKeyboardNavigation)

    //cleanup degli eventi
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('keydown', handleKey)
      window.addEventListener('mousemove', removeKeyboardNavigation)
    }
  }, [])

  useEffect(() => {
    handleResize()
  }, [rows, isLoading, loading, hideFilters, hideSearch, window.innerWidth, window.innerHeight])

  useEffect(() => {
    if (hiddenColumns?.length === columns?.length) onClickCheck([])
  }, [hiddenColumns])

  useEffect(() => {
    if (useReset !== null) executeSearch({}, true)
  }, [useReset])

  useEffect(() => {
    if (lang)
      setColumns(
        TableUtils.updateHeadersOrder(
          headerColumns,
          tableConfiguration.getColumnOrder(),
          tableConfiguration
        )
      )
  }, [lang])

  //determina se ci sono termini di ricerca da resettare
  useEffect(() => {
    let canBeReset = false

    //resetto le selezioni
    select?.fns?.onRemoveAll()

    for (const [key, value] of Object.entries(searchTerms)) {
      if (!canBeReset) {
        const searchObjectKey = initialSearchObject[key]
        canBeReset = searchObjectKey !== value
        //due array vuoti sono sempre diversi, inoltre è necessario verificarne gli elementi
        if (
          canBeReset &&
          Array.isArray(value) &&
          Array.isArray(searchObjectKey) &&
          value.length === searchObjectKey.length
        ) {
          if (value.length === 0) {
            canBeReset = false
          } else {
            canBeReset = !searchObjectKey.every((element, index) => element === value[index])
          }
        }
      }
    }
  }, [searchObject])

  useEffect(() => {
    if (tableConfiguration) {
      tableConfiguration.setHiddenColumns(hiddenColumns)
      tableConfiguration.setColumnOrder(columns)
    }
    handleLayoutChange()
    setHeaderKey(generateUniqueKey(tableConfigurationKey))
  }, [columns])

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (hideSearch === true && isFiltered()) {
      resetAllFilters()
    }
  }, [hideSearch])

  const removeKeyboardNavigation = () => {
    setKeyboardNavigation(false)
  }

  const getMaxIndex = () => {
    const container = document.getElementById(`${tableConfigurationKey}-container`)
    return container ? parseInt(container.offsetHeight / parseInt(height.replace('px', ''))) : 0
  }

  const showDetail = (row) => {
    const timeout = setTimeout(() => {
      if (setId) setId(row.id)
      if (onClickRow) onClickRow(row)
    }, 100)
    setClickTimeout(timeout)
  }

  const handleKey = (event) => {
    const pathName = history.location.pathname
    if (['INPUT', 'BUTTON'].includes(document.activeElement.tagName)) return
    if (!propsRef.current.routePath || propsRef.current.routePath === pathName) {
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        handleKeyArrowUp()
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        handleKeyArrowDown()
      }
    }
  }

  const scrollAuto = (isDown = true) => {
    const container = document.getElementById(`${tableConfigurationKey}-container`)
    if (!container) return
    container.focus()
    const _height = parseInt(height.replace('px', ''))
    container.scrollBy({ top: isDown ? _height : _height * -1, behavior: 'smooth' })
  }

  const handleKeyArrowUp = () => {
    if (propsRef.current.id && propsRef.current.rows?.length > 0) {
      const index = propsRef.current.rows.findIndex((row) => row.id === propsRef.current.id)
      let row
      const previousPage = propsRef.current.searchObject.page - 1
      const lastPage = propsRef.current.totalPages - 1
      if (index - 1 < 0) {
        if (propsRef.current.searchObject.page - 1 < 0) {
          if (lastPage !== propsRef.current.searchObject.page) {
            setSearchObject({ ...propsRef.current.searchObject, page: lastPage })
            setChangePage('up')
          } else {
            row = propsRef.current.rows[propsRef.current.rows.length - 1]
          }
        } else {
          setSearchObject({ ...propsRef.current.searchObject, page: previousPage })
          setChangePage('up')
        }
      } else if (propsRef.current.rows[index - 1]) {
        row = propsRef.current.rows[index - 1]
        if (index < getMaxIndex() && index > 0) scrollAuto(false)
      }
      if (row) {
        if (setId) setId(row.id)
        if (onClickRow) onClickRow(row)
        if (setId || onClickRow) setKeyboardNavigation(true)
      }
    }
  }

  const handleKeyArrowDown = () => {
    if (propsRef.current.id && propsRef.current.rows?.length > 0) {
      const index = propsRef.current.rows.findIndex((row) => row.id === propsRef.current.id)
      let row
      if (index + 1 > propsRef.current.rows.length - 1) {
        if (propsRef.current.searchObject.page + 2 > propsRef.current.totalPages) {
          if (propsRef.current.searchObject.page !== 0) {
            setSearchObject({ ...propsRef.current.searchObject, page: 0 })
            setChangePage('down')
          } else row = propsRef.current.rows[0]
        } else {
          setSearchObject({
            ...propsRef.current.searchObject,
            page: propsRef.current.searchObject.page + 1
          })
          setChangePage('down')
        }
      } else if (propsRef.current.rows[index + 1]) {
        row = propsRef.current.rows[index + 1]
        if (index > getMaxIndex() || index < searchObject.pageSize) scrollAuto()
      }
      if (row) {
        if (setId) setId(row.id)
        if (onClickRow) onClickRow(row)
        if (setId || onClickRow) setKeyboardNavigation(true)
      }
    }
  }

  const handleMouseEnter = (row) => {
    if (useHover) {
      timerRef.current = setTimeout(() => {
        if (isScrolling) {
          setTimeout(() => {
            setIsScrolling(false)
          }, 2000)
          return
        }
        if (setId) setId(row.id)
        if (onClickRow) onClickRow(row)
      }, 400)
    }
  }

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current)
  }

  //aggiunge righe per raggiungere l'altezza minima della tabella
  const prepareRows = () => {
    let rule = ''
    rows.forEach(() => {
      rule += 'auto '
    })
    const minimunRows = 15

    for (let index = rows.length; index < minimunRows; index++) {
      rule += '1fr '
    }

    return rule
  }

  const getComponentHeight = (id) => {
    return document.getElementById(id)?.offsetHeight ? document.getElementById(id).offsetHeight : 0
  }

  // Calcola l'altezza da sottrarre alla tabella
  // altezza da sottrarre = altezza form ancorato + altezza navbar + altezza header + altezza footer + eventuale altezza extra
  const getCalculatedHeight = (formBottom) => {
    const navbarContentHeight = getComponentHeight('navbarContent')
    const headerTableComponentHeight =
      actionsComponentOnTop && actionsComponent ? getComponentHeight('star-table-top') : 0
    const footerHeight = getComponentHeight('footer')
    return (
      formBottom.offsetHeight +
      navbarContentHeight +
      headerTableComponentHeight +
      footerHeight +
      extraOffset
    )
  }

  /**
   * Adegua l'altezza del contenitore della tabella in base alla dimensione dello schermo
   */
  const handleResize = () => {
    let _offset = cloneDeep(offset)
    const pageBody = document.getElementById('page-body')
    // Se esiste un form ancorato in basso alla tabella calcola in automatico la altezza da sottrarre
    const formBottomList = document.getElementsByClassName('form-on-bottom')
    if (formBottomList.length > 0 && formBottomList[0]?.offsetHeight) {
      _offset = getCalculatedHeight(formBottomList[0])
    }
    const _height =
      pageBody.offsetHeight && formBottomList?.[0] ? pageBody.offsetHeight : window.innerHeight
    const tableComponent = document.getElementById(`${tableConfigurationKey}-container`)
    let newHeight = _height - _offset
    // Se l'altezza della tabella è inferiore ad una certa altezza vai in overflow
    if (tableComponent.offsetHeight < breakpointFormOnBottom) newHeight = window.innerHeight
    pageBody.style.removeProperty('height')
    if (tableContainerRef) {
      tableContainerRef.current.style.height = `${newHeight}px`
      const el = document.getElementById(tableConfigurationKey)
      el.style.marginBottom = '0'
      if (tableComponent.offsetHeight < breakpointFormOnBottom) {
        tableContainerRef.current.style.height = '100%'
        // questo margin bottom serve per non farlo sovrapporre al footer
        el.style.marginBottom = '63px'
        if (pageBody) pageBody.style.height = '100%'
      }
    }
  }

  const theme = useTheme([
    getTheme({
      ...DEFAULT_OPTIONS,
      striped: true
    }),
    {
      Cell: `height: ${height} !important;
    &:not(:first-of-type) {
      border-left: 1px solid #a0a8ae;
    }
    `,
      Row: `height: ${height}`,
      Header: `height: 40px;`,
      HeaderCell: `
    max-height:75px;
    padding: 0 !important;
    .table th {padding: 0 5px 0 5px};
    &:not(:first-of-type) {
      border-left: 1px solid #a0a8ae;
    }`,
      Table: `--data-table-library_grid-template-columns: ${headerSizes}; 
    grid-template-rows: ${prepareRows()};
    grid-gap:0px;
    `
    }
  ])

  /**
   * utilizzato alla modifica dei campi in header
   * @param {*} columnName
   * @param {*} query
   */
  const onChangeSearch = (columnName, query) => {
    let newSearchObject = { ...searchObject }
    newSearchObject[columnName] = query
    newSearchObject.page = 0
    clearTimeout(timeout.current)
    setSearchObject(newSearchObject)
    setSearchTerms(TableUtils.getCleanSearchObject(newSearchObject))
    timeout.current = setTimeout(() => {
      executeSearch(newSearchObject)
    }, 600)
  }

  const onDateSearch = (columnName, value, fieldType = fieldTypes.TEXT) => {
    let newSearchObject = { ...searchObject }
    let newDateFilters = { ...dateFilters }
    newDateFilters[columnName] = value
    setDateFilters(newDateFilters)
    if (isEmpty(value)) {
      newSearchObject[columnName] = null
      newSearchObject.page = 0
      setSearchObject(newSearchObject)
      setSearchTerms(TableUtils.getCleanSearchObject(newSearchObject))
      executeSearch(newSearchObject)
      return
    }
    const dates = value.split(/\b(?:al|tra|fra|to)\b/i)
    if (dates.length !== 1 && dates.length !== 2) return
    if (dates.length === 1 && !checkAndGetDate(dates[0].trim())) return
    const from = checkAndGetDate(dates[0].trim())
    const to = dates.length === 1 ? from : checkAndGetDate(dates[1].trim())
    if (
      from?.getTime() === newSearchObject[columnName]?.from?.getTime() &&
      dates.length === 1 &&
      !newSearchObject[columnName]?.to
    ) {
      return
    }
    if (from > to) return
    if (!from || !to) return
    newSearchObject[columnName] = dates.length === 1 ? { from } : { from, to }
    newSearchObject.page = 0
    setSearchObject(newSearchObject)
    setSearchTerms(TableUtils.getCleanSearchObject(newSearchObject))
    if (fieldTypes.DATERANGE == fieldType) {
      newSearchObject.sortField = columnName
      newSearchObject.sortDirection = 'DESC'
    }
    executeSearch(newSearchObject)
  }

  function checkAndGetDate(dateStr) {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/
    const match = dateStr.match(regex)

    if (!match) return null

    const day = parseInt(match[1], 10)
    const month = parseInt(match[2], 10) - 1
    const year = parseInt(match[3], 10)

    const date = new Date(year, month, day)
    date.setHours(12, 0, 0, 0)

    return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day
      ? date
      : null
  }

  const onChangePage = (newSearchObject) => {
    executeSearch(newSearchObject)
  }

  const setElementAfterChangedPage = (list) => {
    const pathName = history.location.pathname
    const isRightPath = !propsRef.current.routePath || propsRef.current.routePath === pathName
    if (changePage === 'up' && list?.[list?.length - 1] && isRightPath) {
      if (onClickRow) onClickRow({ rowData: list[list.length - 1], id: list[list.length - 1].id })
      if (setId) setId(list[list.length - 1].id)
      const container = document.getElementById(`${tableConfigurationKey}-container`)
      if (container) container.scrollTop = container.scrollHeight
    } else {
      const container = document.getElementById(`${tableConfigurationKey}-container`)
      if (container) container.scrollTop = 0
    }
  }

  const executeSearch = (_searchObject, reset = false) => {
    setIsLoading(true)
    localStorage.setItem(
      `${tableConfigurationKey}-page-size`,
      parseInt(_searchObject.pageSize || '20')
    )
    searchCallBack &&
      searchCallBack(_searchObject, reset).then((res) => {
        if (!res) return
        let list = res?.content || res?.data
        if (!usePagination) list = res
        onExecutedSearch(list, res)
        setSearchObject({
          ..._searchObject,
          totalElements: res?.totalElements
        })
        if (usePagination) setTotalPages(res?.totalPages)
        setTimeout(() => setElementAfterChangedPage(list), 50)
        setIsLoading(false)
      })
  }

  const data = { nodes: rows }

  const checkableDataRows = { nodes: data?.nodes.filter((node) => node.checkable === true) }
  const select = useRowSelect(
    checkableDataRows,
    {
      state: { ids: checkedItems },
      onChange: (_, state) => {
        onClickCheck(state?.ids)
      }
    },
    { clickType: SelectClickTypes.ButtonClick }
  )

  const resize = { minWidth: 45, resizerHighlight: '#a6a6a6', resizerWidth: 5 }

  const composeCell = (rowId, data, index, columnIndex, lang) => {
    if ('id' !== data[0]) {
      const cellData = data[1]
      if (cellData) {
        const header = columns.find((h) => h.fieldName === cellData.fieldName)

        const hide = TableUtils.isHiddenColumn(hiddenColumns, header)

        return (
          <StarCell
            key={`starcell-${rowId}-${index} `}
            idToCompare={id}
            lang={lang}
            rowId={rowId}
            cellData={cellData}
            index={index}
            header={header}
            hide={hide}
          />
        )
      }
    }
    return null
  }

  const handleLayoutChange = () => {
    if (tableConfiguration) {
      const styles = getComputedStyle(tableRef.current)

      const sizes =
        styles.getPropertyValue('--data-table-library_grid-template-columns') ||
        tableConfiguration.getColumnSizes()
      try {
        if (initialColumns?.length === 0) return
        if (sizes !== '') {
          let matches = sizes.match(/minmax\([^)]*\)|\d+px/g)
          const checkbox = withCheckBoxes ? matches.shift() : undefined
          matches = matches.map((size, index) => {
            return { size: size, fieldName: initialColumns[index].fieldName }
          })
          setInitialColumns(columns)
          const newColumnSizes = []
          columns.forEach((column) => {
            const columnSize = matches.find((size) => size.fieldName === column.fieldName)
            newColumnSizes.push(columnSize.size)
          })
          if (withCheckBoxes) newColumnSizes.unshift(checkbox)
          const _sizes = newColumnSizes.join(' ')
          tableConfiguration.setColumnSizes(_sizes)
          tableRef.current.style.setProperty('--data-table-library_grid-template-columns', _sizes)
        } else {
          tableConfiguration.setColumnSizes(headerSizes)
        }
      } catch (e) {
        console.error(e)
        tableConfiguration.setColumnSizes(headerSizes)
        tableRef.current.style.setProperty(
          '--data-table-library_grid-template-columns',
          headerSizes
        )
      }
    }
  }

  const renderSortButtons = (searchObject, h, defaultSort) => {
    const orderingName = h.orderingName || h.fieldName
    return (
      <span>
        {searchObject.sortField === orderingName && (
          <>
            {searchObject.sortDirection === 'DESC' ? (
              <i className="star-icon-sort-down star-icon cursor-pointer float-end mb-1" />
            ) : (
              <i className="star-icon-sort-up star-icon cursor-pointer float-end mb-1" />
            )}
          </>
        )}
      </span>
    )
  }

  const sort = (searchObject, h, defaultSort, e) => {
    e?.stopPropagation()
    const orderingName = h.orderingName || h.fieldName
    // l'utente clicca un campo diverso da quello già ordinato
    if (!useSort) return
    if (searchObject.sortField !== orderingName) {
      onChangePage({ ...searchObject, sortField: orderingName, sortDirection: 'ASC' })
    } else {
      if (searchObject.sortDirection === 'DESC') {
        onChangePage({ ...searchObject, sortField: defaultSort, sortDirection: 'DESC' })
      } else {
        onChangePage({ ...searchObject, sortField: orderingName, sortDirection: 'DESC' })
      }
    }
  }

  const drag = () => {
    const element = tableRef.current.querySelector('thead tr:nth-of-type(1)')
    if (!element || !useDrag) return
    Sortable.create(element, {
      filter: '.checkbox-column',
      onEnd(event) {
        let oldIndex, newIndex
        if (withCheckBoxes) {
          oldIndex = event.oldIndex - 1
          newIndex = event.newIndex - 1
        } else {
          oldIndex = event.oldIndex
          newIndex = event.newIndex
        }
        const newColumns = [...columns]
        const element = newColumns.splice(oldIndex, 1)[0]
        newColumns.splice(newIndex, 0, element)
        setColumns(newColumns)
      },
      onMove: function (evt) {
        if (evt.related.className.includes('checkbox-column')) {
          return false
        }
      }
    })
  }

  const resetAllFilters = () => {
    setDateFilters({})
    executeSearch(
      {
        ...initialSearchObject,
        totalElements: searchObject.totalElements,
        pageSize: searchObject.pageSize || '20'
      },
      true
    )
  }

  const removeEmptyValue = (_searchObject) => {
    delete _searchObject.totalElements
    delete _searchObject.pageSize
    delete _searchObject.page
    delete _searchObject.parentId
    delete _searchObject.version
    delete _searchObject.sortDirection
    delete _searchObject.sortField
    Object.keys({ ..._searchObject }).forEach((key) => {
      if (_searchObject[key] === '' || _searchObject[key]?.length === 0 || !_searchObject[key]) {
        delete _searchObject[key]
      }
    })
    return _searchObject
  }

  const isFiltered = () => {
    const _searchObject = removeEmptyValue({ ...searchObject })
    const _initialSearchObject = removeEmptyValue({ ...initialSearchObject })
    return (
      !isEqual(_initialSearchObject, _searchObject) ||
      !['id', '', null, undefined].includes(searchObject.sortField)
    )
  }

  const showOrHideFilters = () => {
    let hide = hideSearch !== null ? !hideSearch : !hideFilters
    if (!columns.some((c) => c.searchable === true)) hide = true
    if (hideSearch !== null && onHideSearch) onHideSearch(hide)
    else setHideFilters(hide)
  }

  const _loading = isLoading || loading

  return (
    <div id={tableConfigurationKey} className="star-table">
      {!formOnBottom && actionsComponentOnTop && (
        <div className="d-flex py-1 star-table-top-container">
          <StarTableTop
            loading={_loading}
            language={lang || 'it'}
            actionsComponent={actionsComponent}
            showResetFilter={!resetFilterOnBottom && showResetFilter}
            columns={columns}
            showDrag={useDrag}
            showPagination={!DynamicFormComponent && !paginationOnBottom}
            hiddenColumns={hiddenColumns}
            isFiltered={isFiltered()}
            searchObject={searchObject}
            totalPages={totalPages}
            tableConfiguration={tableConfiguration}
            tableConfigurationKey={tableConfigurationKey}
            withCheckBoxes={withCheckBoxes}
            perPageOptions={perPageOptions}
            setColumns={setColumns}
            usePagination={usePagination}
            setHiddenColumns={setHiddenColumns}
            setHeaderSizes={setHeaderSizes}
            setTableKey={setTableKey}
            onChangePage={onChangePage}
            onResetFilters={resetAllFilters}
          />
          {headerComponent}
        </div>
      )}
      <div
        id={`${tableConfigurationKey}-container`}
        className="d-flex flex-column m-auto overflow-auto outline-none">
        <div
          ref={tableContainerRef}
          className="star-table-container"
          style={{ width: tableContainerWidth }}>
          {dialogComponent}

          <Table
            key={tableKey}
            theme={theme}
            className={`${_loading ? 'no-grid table-loading' : ''} ${keyboardNavigation ? 'keyboard-navigation' : ''} overflow-unset`}
            data={data}
            select={select}
            ref={tableRef}
            layout={{
              custom: true,
              fixedHeader: true,
              onLayoutChange: () => handleLayoutChange()
            }}>
            {(tableList) => (
              <>
                <Header key={headerKey}>
                  <HeaderRow>
                    {withCheckBoxes && (
                      <HeaderCell
                        hide={hiddenColumns?.length === columns?.length}
                        pinLeft
                        className="checkbox-column table-header">
                        <StarCheckbox
                          checked={select?.state?.all}
                          disabled={!rows?.length > 0}
                          indeterminate={!select?.state?.all && !select?.state?.none}
                          onChange={select?.fns?.onToggleAll}
                          ariaLabel={'Seleziona tutti i visibili'}
                        />
                      </HeaderCell>
                    )}
                    {columns.map((h, index) => {
                      const component = h?.component(
                        sort,
                        searchObject,
                        h,
                        defaultSort,
                        additionalHeaderProps
                      )
                      return (
                        <HeaderCell
                          key={`header - ${index} `}
                          className="table-header"
                          resize={useResize ? resize : undefined}
                          hide={TableUtils.isHiddenColumn(hiddenColumns, h)}
                          pinRight={h?.pinRight}
                          pinLeft={h?.pinLeft}
                          onMouseEnter={drag}>
                          <div>
                            <div
                              className="d-flex flex-column justify-content-center w-100"
                              style={useDrag ? { cursor: 'grab' } : {}}>
                              <div
                                className={`star-table-header ${h.additionalClass}`}
                                onClick={(e) => showOrHideFilters()}>
                                <div className="d-flex">
                                  <div onClick={(e) => sort(searchObject, h, defaultSort, e)}>
                                    {h.sortable && renderSortButtons(searchObject, h, defaultSort)}
                                  </div>
                                  {!component ? (
                                    <span
                                      onClick={(e) =>
                                        h.sortable ? sort(searchObject, h, defaultSort, e) : {}
                                      }
                                      className={h.sortable && useSort ? 'cursor-pointer' : ''}
                                      style={h.sortable && useSort ? {} : sortStyle}>
                                      {h.displayedName}
                                    </span>
                                  ) : (
                                    component
                                  )}
                                </div>
                              </div>

                              <div
                                className="star-table-searchfield"
                                style={
                                  (hideSearch !== null ? hideSearch : hideFilters)
                                    ? { display: 'None' }
                                    : {}
                                }>
                                {h.searchable && (
                                  <TableSearchField
                                    type={h.type}
                                    hideSearch={hideSearch}
                                    dateFilters={dateFilters}
                                    fieldName={h.fieldName}
                                    value={searchObject[h.fieldName]}
                                    options={headerColumns[index].options}
                                    lang={lang}
                                    onDate={(value) => onDateSearch(h.fieldName, value, h.type)}
                                    onChange={(value) => onChangeSearch(h.fieldName, value)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </HeaderCell>
                      )
                    })}
                  </HeaderRow>
                </Header>
                <Body>
                  <>
                    {_loading && (
                      <TableSkeleton
                        colCount={baseSearchObject.pageSize || 20}
                        rowsCount={
                          withCheckBoxes
                            ? columns.length - hiddenColumns.length + 1
                            : columns.length - hiddenColumns.length
                        }
                      />
                    )}

                    {!_loading &&
                      hiddenColumns?.length !== columns?.length &&
                      rows?.length === 0 &&
                      columns?.length > 0 && (
                        <tr className="star-table-empty-state">
                          <td>
                            <i
                              className="star-icon-warning-triangle star-icon-warning-triangle-grey star-icon"
                              style={{ width: '56px', height: '56px' }}
                            />
                            <h4>{labels.NO_RESULTS}</h4>
                            {isFiltered() && (
                              <button
                                className="btn btn-primary mt-2"
                                onClick={() => resetAllFilters()}>
                                {labels.RESET_ALL_FILTERS}
                              </button>
                            )}
                          </td>
                        </tr>
                      )}

                    {!isLoading &&
                      !loading &&
                      columns?.length > 0 &&
                      hiddenColumns?.length === columns?.length && (
                        <div className="empty-state">
                          <i className="star-icon-table star-icon-table-grey star-icon" />
                          <h4>Nessuna colonna selezionata</h4>
                          <button
                            className="btn btn-primary mt-2"
                            onClick={() => tableConfiguration.cleanSearchObject()}>
                            {labels.RESTORE}
                          </button>
                        </div>
                      )}

                    {!isLoading &&
                      !loading &&
                      columns?.length > 0 &&
                      hiddenColumns?.length !== columns?.length &&
                      tableList.map((row, index) => {
                        const rowData = row.cellData
                        return (
                          <Row
                            key={'star-row-' + row.id}
                            id={tableConfigurationKey + '-star-row-' + index}
                            item={rowData}
                            style={_loading ? { display: 'None' } : {}}
                            className={`${row.additionalClass} ${rowClass(row.rowData)}`}
                            onMouseEnter={() => handleMouseEnter(row)}
                            onMouseLeave={handleMouseLeave}
                            onDoubleClick={() => onDoubleClickRow(row)}
                            onClick={() => showDetail(row)}>
                            {/*disegno la checkbox solo se prevista*/}
                            {withCheckBoxes && row.checkable && (
                              <Cell
                                pinLeft
                                item={row}
                                className={`checkbox-column ${row.id === id ? 'bg-primary-rgb' : ''}`}>
                                <StarCheckbox
                                  checked={select.state.ids.includes(row.id)}
                                  onChange={() => {
                                    select.fns.onToggleById(row.id)
                                  }}
                                  ariaLabel={'Seleziona elemento'}
                                />
                              </Cell>
                            )}
                            {withCheckBoxes && !row.checkable && (
                              <Cell
                                pinLeft
                                item={row}
                                className={`checkbox-column ${row.id === id ? 'bg-primary-rgb' : ''}`}
                              />
                            )}
                            {
                              //per preservare l'ordine delle celle e delle colonne, viene eseguito un doppio ciclo
                              !isLoading &&
                                !loading &&
                                columns.map((column, columnIndex) => {
                                  return Object.entries(rowData).map((e, index) => {
                                    if (e[1]?.fieldName === column.fieldName) {
                                      return composeCell(row.id, e, index, columnIndex, lang)
                                    }
                                  })
                                })
                            }
                          </Row>
                        )
                      })}
                  </>
                </Body>
              </>
            )}
          </Table>
        </div>
      </div>
      {DynamicFormComponent && (
        <DynamicFormComponent
          {...formComponentProps}
          paginationComponent={
            <StarTableTop
              loading={_loading}
              columns={columns}
              language={lang || 'it'}
              isFiltered={isFiltered()}
              showResetFilter={resetFilterOnBottom && showResetFilter}
              hiddenColumns={hiddenColumns}
              searchObject={searchObject}
              totalPages={totalPages}
              tableConfiguration={tableConfiguration}
              tableConfigurationKey={tableConfigurationKey}
              withCheckBoxes={withCheckBoxes}
              perPageOptions={perPageOptions}
              setColumns={setColumns}
              usePagination={usePagination}
              setHiddenColumns={setHiddenColumns}
              setHeaderSizes={setHeaderSizes}
              setTableKey={setTableKey}
              onResetFilters={resetAllFilters}
              onChangePage={onChangePage}
            />
          }
        />
      )}
      {paginationOnBottom && !DynamicFormComponent && (
        <div className="d-flex pb-1 pt-2 ps-1 star-table-top-container">
          <StarTableTop
            loading={_loading}
            language={lang || 'it'}
            actionsComponent={actionsComponent}
            showResetFilter={!resetFilterOnBottom && showResetFilter}
            columns={columns}
            showDrag={useDrag}
            extraComponentBottomLeft={extraComponentBottomLeft}
            paginationOnLeft={true}
            showPagination={true}
            hiddenColumns={hiddenColumns}
            isFiltered={isFiltered()}
            searchObject={searchObject}
            totalPages={totalPages}
            tableConfiguration={tableConfiguration}
            tableConfigurationKey={tableConfigurationKey}
            withCheckBoxes={withCheckBoxes}
            perPageOptions={perPageOptions}
            setColumns={setColumns}
            usePagination={usePagination}
            setHiddenColumns={setHiddenColumns}
            setHeaderSizes={setHeaderSizes}
            setTableKey={setTableKey}
            onChangePage={onChangePage}
            onResetFilters={resetAllFilters}
          />
        </div>
      )}
    </div>
  )
}

const getDateRangePlaceholder = (value = {}, lang) => {
  const from = formatDateForDisplay(value?.from)
  const to = formatDateForDisplay(value?.to)
  if (from && !to) return from
  if (from && to) return `${from} ${lang === 'it' ? 'al' : 'to'} ${to}`
  return lang === 'it' ? 'gg/mm/aaaa al gg/mm/aaaa' : 'dd/mm/yyyy to dd/mm/yyyy'
}

function TableSearchField({
  type = fieldTypes.TEXT,
  onDate = () => {},
  onChange = () => {},
  options = [{ id: null, description: '' }],
  fieldName,
  hideSearch,
  dateFilters,
  value = '',
  lang = 'it'
} = {}) {
  if (fieldTypes.CURRENCY === type) {
    return (
      <NumberFormat
        thousandSeparator={'.'}
        decimalSeparator={','}
        decimalScale={0}
        fixedDecimalScale={true}
        className="form-control form-control-cell"
        inputMode="numeric"
        value={value}
        onValueChange={(value) => onChange(value.floatValue)}
      />
    )
  } else if (fieldTypes.DATERANGE === type) {
    if (hideSearch) return <div></div>
    return (
      <StarTooltip text={getDateRangePlaceholder(value, lang)}>
        <div className="daterange-filter">
          <input
            id={fieldName}
            value={dateFilters[fieldName]}
            autoComplete="off"
            placeholder={getDateRangePlaceholder(value, lang)}
            className={`form-control form-control-date ${value ? 'has-value' : ''}`}
            onChange={(el) => onDate(el.target.value)}
          />
          {value && (
            <button
              onClick={() => {
                const input = document.getElementById(fieldName)
                input.value = ''
                onDate()
              }}>
              <i className="star-icon-cancel star-icon" />
            </button>
          )}
        </div>
      </StarTooltip>
    )
  } else if (fieldTypes.SINGLE_SELECTION === type) {
    return (
      <BwmSelect
        value={value}
        isCell={true}
        isClearable={true}
        options={options}
        onChange={(value) => onChange(value?.id && value?.id !== '' ? value?.id : null)}
      />
    )
  } else if (fieldTypes.SELECTION === type) {
    return (
      <DropdownMultipleSelect
        options={options}
        value={options.filter((o) => {
          if (value && value.length > 0) {
            return value.includes(o.id)
          }
          return false
        })}
        onSelectionChange={(options) => onChange(options.map((o) => o.id))}
      />
    )
  } else {
    return (
      <div>
        <input
          type={
            [fieldTypes.DATE, fieldTypes.TEXT].includes(type) ? fieldTypes.TEXT : fieldTypes.NUMBER
          }
          className="form-control form-control-cell"
          placeholder={lang === 'it' ? `Cerca...` : `Search...`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
        {value && (
          <button className="btn-clear-input" onClick={() => onChange()}>
            <i className="star-icon-cancel star-icon" />
          </button>
        )}
      </div>
    )
  }
}
